/**
 * The Chat surface — the composer, the message stream, and the panels that open beside them.
 *
 * One half of `AGENT_COPY`. The shape and the rules are documented on the
 * barrel in `../agent-copy.ts`; this file only holds entries.
 */
export const CHAT: Record<string, string> = {
	Agent: "智能体",
	Agents: "智能体",
	"All agents": "全部智能体",
	Templates: "模板",
	Draft: "未发布",
	"Last modified": "上次修改时间",
	"Last run": "上次运行",
	"Last run failed": "上次运行失败",
	"New agent": "新建智能体",
	"No agents here yet. Create an agent or start from a template.": "这里还没有智能体。可以新建智能体，或从模板开始。",
	"No folder": "不放入文件夹",
	"No matching agents.": "未找到匹配的智能体。",
	"No scheduled agents. Open an agent and use Schedule to set its run times.":
		"还没有定时运行的智能体。打开智能体，点击“定时”设置运行时间。",
	Published: "已发布",
	"Scheduled agents": "已计划",
	"Search agents": "搜索智能体",
	"Search templates": "搜索模板",
	"Untitled workflow": "未命名工作流",

	// -- the empty chat: the greeting and the composer's placeholder
	"What should we get done, {name}?": "{name}，今天做点什么？",
	"What should we get done?": "今天做点什么？",
	"Ask Ondit to ": "让 Ondit ",
	"find the questions I am missing from...": "找出我漏掉的提问……",
	"explain why my citation share fell...": "解释我的引用占比为什么下降……",
	"draft a page for the gap a rival wins...": "为对手占优的缺口起草页面……",
	"check which engines cite my domain...": "看看哪些引擎引用了我的域名……",
	"Send message to Ondit": "向 Ondit 发送消息",
	"Search your documents…": "搜索你的文档…",
	"Ask about your documents or take action…": "提问你的文档，或让它执行…",

	// -- the catalogue sections under the composer
	"My agents": "我的智能体",
	"Recently updated": "最近更新",
	More: "更多",
	"Previous page": "上一页",
	"Next page": "下一页",
	"Page {page} of {pages}": "第 {page} / {pages} 页",
	"Nothing here yet.": "这里还没有内容。",

	// -- suggested actions under the composer
	"Suggested actions": "推荐操作",
	Sources: "来源",
	"Integrate with {name}": "接入 {name}",
	"Connect tools": "连接工具",
	"Connect your tools to give Ondit more context.": "连接常用工具，让 Ondit 更了解你的工作。",
	"Explain why my citation share moved": "解释我的引用占比为什么变化",
	"Compare my citation share with the period before it and explain what changed. Name the answers and the domains that moved it, and say which of them I can do something about.":
		"把我的引用占比与上一周期对比，说明变化来自哪里。指出造成变化的回答和域名，并说明其中哪些是我能改变的。",
	"Which engine should I work on first": "该先做哪个引擎",
	"Break my visibility down by engine for the current period and tell me which one to work on first. Say what makes that engine different from the others, using the answers it actually returned.":
		"按引擎拆解本周期的可见度，告诉我该先做哪一个。用该引擎实际返回的回答，说明它与其他引擎的差别。",
	"Find the questions I am missing": "找出我漏掉的提问",
	"List the tracked questions where a competitor is named and my brand is not. For each, show what the answer cited instead and say what a page would have to establish to be cited there.":
		"列出被跟踪的提问中，提到竞品却没提到我的品牌的那些。逐条给出回答实际引用了什么，并说明一个页面要立住哪些事实才可能被引用。",
	"Who is cited instead of me": "谁被引用而不是我",
	"List the domains cited most often in answers to my tracked questions, mark which of them are competitors, and say for each what kind of page is being cited.":
		"列出在我跟踪的提问的回答中被引用最多的域名，标出其中的竞品，并逐条说明被引用的是哪类页面。",
	"Draft a page for my biggest gap": "为最大的缺口起草页面",
	"Take the open gap with the most answers behind it and draft a page for it from the facts on file. Show me the fact table beside the draft so I can check every sentence.":
		"选出背后回答数最多的未处理缺口，用已归档的事实起草一个页面。把事实表放在草稿旁边，让我逐句核对。",
	"Check my published pages still hold": "检查已发布页面是否仍然成立",
	"Review the pages I have live: flag any whose facts are older than the datapoints they came from, and list what would have to change.":
		"检查已上线的页面：标出其中事实早于来源数据点的页面，并列出需要改动的内容。",
	"Create a CRM with sample data": "创建带示例数据的 CRM",
	"Create a CRM with sample data.": "创建一个带示例数据的 CRM。",
	"Build a project tracker": "搭建项目跟踪表",
	"Build a project tracker table.": "搭建一个项目跟踪表。",
	"Create a content calendar": "创建内容日历",
	"Create a content calendar table.": "创建一个内容日历表。",
	"Build an expense tracker": "搭建费用跟踪表",
	"Build an expense tracker table.": "搭建一个费用跟踪表。",
	"Create a bug tracker": "创建缺陷跟踪表",
	"Create a bug tracker table.": "创建一个缺陷跟踪表。",

	// -- the composer's toolbar
	"Add resources": "添加资源",
	"Attach file": "添加附件",
	"No skills or MCP servers": "没有技能或 MCP 服务",
	"Search resources...": "搜索资源...",
	"Send message": "发送消息",
	"Stop generation": "停止生成",
	"Voice input": "语音输入",
	"Stop listening": "停止录音",
	"Drop files": "拖放文件",
	"Remove {name}": "移除 {name}",
	"Analyze the attached file(s).": "分析所附文件。",
	"Mode: {mode}": "模式：{mode}",
	Build: "构建",
	Search: "搜索",
	Assistant: "助手",

	// -- microphone permission
	"Allow microphone access": "允许使用麦克风",
	"Once microphone access is blocked, your browser requires you to change it from the site controls.":
		"麦克风一旦被拒绝，就只能在浏览器的网站权限里重新打开。",
	"Open the site controls beside the address bar.": "点开地址栏旁边的网站权限。",
	"Set Microphone access for this site to Allow.": "把本站的“麦克风”设为允许。",
	"Reload the page if prompted, then try voice input again.": "如有提示请刷新页面，然后重试语音输入。",
	"In Safari, open Safari Settings, then Websites, then Microphone.": "Safari 请依次打开“设置”“网站”“麦克风”。",
	"Microphone access is blocked. Allow Ondit to use the microphone in your system privacy settings.":
		"麦克风被拒绝。请在系统隐私设置中允许 Ondit 使用麦克风。",
	"Microphone access is blocked. Allow it for this site and try again.": "麦克风被拒绝。请在本站的权限里允许后重试。",
	"No microphone found. Connect one and try again.": "没有找到麦克风，请接入后重试。",
	"Could not start voice input. Try again.": "无法开始语音输入，请重试。",
	"Show steps": "查看步骤",
	"Open Settings": "打开设置",

	// -- credits
	"You are out of credits.": "积分已用完。",

	// -- the queue above the composer
	"{count} Queued": "已排队 {count} 条",
	"Cancel edit": "取消编辑",
	"Edit queued message": "编辑排队消息",
	"Sending now": "正在发送",
	"Send now": "立即发送",
	"Remove from queue": "移出队列",

	// -- the message stream: agent lanes and the turn's shimmer
	"Thinking…": "思考中…",
	"Dispatching…": "派发中…",
	"Returning…": "返回中…",
	"Stopped by user": "已由用户停止",
	"Workflow Agent": "工作流智能体",
	"Debug Agent": "调试智能体",
	"Deploy Agent": "部署智能体",
	"Auth Agent": "授权智能体",
	"Research Agent": "研究智能体",
	"Knowledge Agent": "知识智能体",
	"Table Agent": "表格智能体",
	"Custom Tool Agent": "自定义工具智能体",
	"Scout Agent": "探查智能体",
	"Search Agent": "搜索智能体",
	"Platform Agent": "平台智能体",
	Superagent: "总控智能体",
	"Run Agent": "运行智能体",
	"Extensions Agent": "扩展智能体",
	"Job Agent": "任务智能体",
	"File Agent": "文件智能体",
	"Media Agent": "媒体智能体",
	"Browser Agent": "浏览器智能体",

	// -- tool rows and the permission card
	"Creating {name}": "正在创建 {name}",
	"Adding {name}": "正在追加 {name}",
	"Editing {name}": "正在修改 {name}",
	"Writing {name}": "正在写入 {name}",
	"Renaming {name}": "正在重命名 {name}",
	"Deleting {name}": "正在删除 {name}",
	Allow: "允许",
	"Don't ask again": "不再询问",
	"For this chat": "本次对话",
	"For every chat": "所有对话",
	Skip: "跳过",
	"Allow all": "全部允许",
	"Skip all": "全部跳过",
	"{count} tools need permission": "有 {count} 个工具待授权",
	"{title} — this request is no longer active": "{title} — 该请求已失效",
	"Could not record your choice. Please try again.": "无法记录你的选择，请重试。",

	// -- question and options cards in the stream
	"Suggested follow-ups": "推荐追问",
	"Previous question": "上一题",
	"Next question": "下一题",
	"{step} of {total}": "第 {step} 题，共 {total} 题",
	Dismiss: "关闭",
	"Something else": "其他",
	'Include "Something else" in the answer': "把“其他”计入答案",
	"Submit answer": "提交答案",
	Submit: "提交",
	Continue: "继续",

	// -- cited sources under a reply
	"1 source": "1 条来源",
	"{count} sources": "{count} 条来源",
	Summarize: "总结",
	'Summarize "{title}"': "总结“{title}”",

	// -- Search mode results
	"Search couldn’t run.": "搜索没能执行。",
	"Retrying…": "重试中…",
	"No sources are set up yet.": "还没有配置任何来源。",
	"View sources": "查看来源",
	"Searching…": "搜索中…",
	"1 document": "1 篇文档",
	" · searched as you": " · 以你的权限搜索",
	"Still indexing {sources}; results grow as documents land.": "{sources} 仍在建立索引，文档陆续到达后结果会变多。",
	"All sources": "全部来源",
	Uploads: "上传",
	"Any time": "不限时间",
	"Past week": "近一周",
	"Past month": "近一月",
	"No documents match these filters.": "没有文档符合当前筛选。",
	"No documents you can read match “{query}”.": "你有权限查看的文档中没有匹配“{query}”的。",

	// -- connecting your own search sources
	"Connect {name}": "连接 {name}",
	"Ask a workspace admin to connect {name}": "请工作区管理员连接 {name}",
	Connect: "连接",
	"Connecting…": "连接中…",
	"Verify email": "验证邮箱",
	"Access removed": "访问已撤销",
	"Setup guide": "配置指南",

	// -- credential and access cards in the stream
	"Set up credentials": "配置凭据",
	"Connect integrations": "连接集成",
	"Add secrets": "添加密钥",
	"Paste {name}": "粘贴 {name}",
	"Saved {name}": "已保存 {name}",
	"Couldn't save {name}. Please try again.": "{name} 保存失败，请重试。",
	"Couldn't save secrets. Please try again.": "密钥保存失败，请重试。",
	Added: "已添加",
	Secret: "密钥",
	"Grant access to a local folder": "授权访问本地文件夹",
	"Grant access to {name}": "授权访问 {name}",
	"Access granted — {name}": "已授权 — {name}",
	"Granted access to {name}": "已授权访问 {name}",
	"Choose a folder…": "选择文件夹…",
	"Couldn't open the folder picker. Please try again.": "无法打开文件夹选择器，请重试。",
	"Finish in the browser": "请在浏览器中完成",
	"Finish in the terminal, then hand control back": "请在终端中完成，然后交还控制权",
	"Handed control back to Ondit": "已把控制权交还 Ondit",

	// -- the resource panel beside the chat
	"Expand resource view": "展开资源面板",
	"Collapse resource view": "收起资源面板",
	"Expand resource view, 1 resource updated": "展开资源面板，1 个资源有更新",
	"Expand resource view, {count} resources updated": "展开资源面板，{count} 个资源有更新",
	"Resize resource panel": "调整资源面板宽度",
	"Add resource": "添加资源",
	"Add resource tab": "添加资源标签页",
	"Cycle preview mode": "切换预览模式",
	"Split Mode": "分栏模式",
	"Preview Mode": "预览模式",
	"Edit Mode": "编辑模式",
	Results: "结果",
	"Knowledge Bases": "知识库",
	"File Folders": "文件目录",
	Chats: "对话",
	Browser: "浏览器",
	Terminal: "终端",

	// -- what a resource tab can do
	"Run workflow": "运行工作流",
	"Open knowledge base": "打开知识库",
	"Open table": "打开表格",
	"Export table as CSV": "导出表格为 CSV",
	"Export CSV": "导出 CSV",
	"Open in files": "在文件中打开",
	"Download file": "下载文件",
	"Open in logs": "在日志中打开",
	"Workflow not found": "找不到该工作流",
	"This workflow may have been deleted or moved": "该工作流可能已被删除或移动",
	"File not found": "找不到该文件",
	"This file may have been deleted or moved": "该文件可能已被删除或移动",
	"Folder not found": "找不到该文件夹",
	"This folder may have been deleted or moved": "该文件夹可能已被删除或移动",
	"No workflows in this folder": "该文件夹中没有工作流",
	"Log not found": "找不到该日志",
	"This log may have been deleted or is no longer available": "该日志可能已被删除或不再可用",
	"No results yet": "还没有结果",
	Rejected: "已拒绝",

	// -- the browser panel
	Back: "后退",
	Forward: "前进",
	"Reload page": "刷新页面",
	Reload: "刷新",
	"Search Google or enter a URL": "用 Google 搜索或输入网址",
	"Search Google or enter a URL — press Enter": "用 Google 搜索或输入网址 — 按回车",
	"New tab": "新建标签页",
	"Browse the web": "浏览网页",
	"Search or enter a URL above.": "在上方搜索或输入网址。",
	"Waiting for the browser session to start…": "正在等待浏览器会话启动…",
	"Browser menu": "浏览器菜单",
	"Find in Page": "在页面中查找",
	"Find in page": "在页面中查找",
	"Previous match": "上一个匹配",
	"Next match": "下一个匹配",
	"Close find bar": "关闭查找栏",
	"Import Passwords": "导入密码",
	"Browser Settings": "浏览器设置",
	"Fill a saved password": "填入已保存的密码",
	"(no username)": "（无用户名）",
	Downloads: "下载内容",
	"Downloads, {count} in progress": "下载内容，{count} 项进行中",
	"Open in External Browser": "在外部浏览器中打开",
	"Could not open this page in your browser.": "无法在你的浏览器中打开该页面。",
	"Could not open a new browser tab. Please try again.": "无法新建浏览器标签页，请重试。",
	"Some sites apply theme changes after a reload.": "部分网站需要刷新后才会应用主题变化。",
	"Dismiss theme notice": "关闭主题提示",
	"Page loading": "页面加载中",
	"Page loaded": "页面已加载",
	"Loading page": "正在加载页面",

	// -- what the browser asks before it lets a page do something
	"Open another website?": "打开另一个网站？",
	"This page wants to open {origin}. Allow this destination for the rest of this browser task? Only continue if you trust this website.":
		"该页面想打开 {origin}。要在本次浏览任务中一直允许这个目标地址吗？只有信任该网站时才继续。",
	"Allow {origin} to use your {devices}?": "允许 {origin} 使用你的 {devices}？",
	"Allowing gives this page access to your {devices} until it navigates. Block if you did not expect this request.":
		"允许后，该页面在跳转前都可以使用你的 {devices}。如果这不是你预期的请求，请拒绝。",
	Block: "拒绝",

	// -- what a page says when it fails to load
	"The site": "该网站",
	"This page crashed": "页面已崩溃",
	"{host} ran into a problem and closed unexpectedly.": "{host} 出现问题并意外关闭。",
	"Reloading the page": "刷新页面",
	"Closing other tabs if this keeps happening": "如果反复出现，关闭其他标签页",
	"This page isn't responding": "页面没有响应",
	"{host} stopped responding.": "{host} 停止了响应。",
	"Waiting a moment": "稍等片刻",
	"Your connection isn't private": "你的连接不是私密连接",
	"The security certificate for {host} could not be verified.": "无法验证 {host} 的安全证书。",
	"Checking your device clock": "检查设备时钟",
	"Contacting the site administrator": "联系网站管理员",
	"This site can't be reached": "无法访问此网站",
	"The configured proxy server could not be reached.": "无法连接所配置的代理服务器。",
	"Checking the proxy settings": "检查代理设置",
	"Checking the network connection": "检查网络连接",
	"{host} is unavailable from this network.": "在当前网络下无法访问 {host}。",
	"Checking the address": "检查网址",
	"Checking firewall and network settings": "检查防火墙和网络设置",
	"{host} refused to connect.": "{host} 拒绝了连接。",
	"Checking the connection": "检查连接",
	"{host} could not be found.": "找不到 {host}。",
	"Checking the DNS and network connection": "检查 DNS 和网络连接",
	"You're offline": "你处于离线状态",
	"Check your internet connection and try again.": "请检查网络连接后重试。",
	"Checking network cables and Wi-Fi": "检查网线和 Wi-Fi",
	"Reconnecting to the internet": "重新连接网络",
	"{host} took too long to respond.": "{host} 响应超时。",
	"Trying again in a moment": "稍后重试",
	"{host} could not be reached.": "无法连接 {host}。",
	"Try:": "可以试试：",

	// -- the terminal panel
	"New terminal": "新建终端",
	"New Tab": "新建标签页",
	"Close Terminal": "关闭终端",
	"Terminal Settings": "终端设置",
	"Terminal theme": "终端主题",
	"Add to chat": "添加到对话",
	Theme: "主题",
	Default: "默认",
	"Ondit Light": "Ondit 浅色",
	"Ondit Dark": "Ondit 深色",
	"Zoom In": "放大",
	"Zoom Out": "缩小",
	"Actual Size": "实际大小",
	"Paste is too large for the terminal": "粘贴内容超出终端上限",
	"Paste up to {limit} at once, or send the content through a file.": "单次最多粘贴 {limit}，更长的内容请用文件传入。",
	"Could not paste from the clipboard. Press ⌘V to paste.": "无法读取剪贴板，请按 ⌘V 粘贴。",
	"Could not open a new terminal. Please try again.": "无法新建终端，请重试。",
	"Could not switch terminals. Please try again.": "无法切换终端，请重试。",
	"Could not reorder that terminal. Please try again.": "无法调整该终端的顺序，请重试。",
	"Could not duplicate that terminal. Please try again.": "无法复制该终端，请重试。",
	"Could not close that terminal. Please try again.": "无法关闭该终端，请重试。",
	"Could not close one of those terminals. Please try again.": "其中一个终端关闭失败，请重试。",
	"{command} is still running. Close this terminal and stop it?": "{command} 仍在运行。关闭该终端并停止它吗？",
	"1 selected terminal has a running process. Close it anyway?": "所选的 1 个终端仍有进程在运行，仍要关闭吗？",
	"{count} selected terminals have a running process. Close them anyway?":
		"所选的 {count} 个终端仍有进程在运行，仍要关闭吗？",

	// -- the screen failed to load
	"Failed to load home": "首页加载失败",
	"Something went wrong while loading your workspace home. Please try again.": "加载工作区首页时出错，请重试。",
	"Failed to load chat": "对话加载失败",
	"Something went wrong while loading this chat. Please try again.": "加载该对话时出错，请重试。",
};
